import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { formatUnits, formatRate } from '../../lib/reporting-format';

export type TopMover = {
  sku: string;
  type: string | null;
  color: string | null;
  size: string | null;
  units: number;
  velocity: number;
  prevUnits: number;
  deltaUnits: number;
  trend: 'rising' | 'falling' | 'flat';
};

interface TopMoversCardProps {
  loading: boolean;
  rows: TopMover[];
}

const TREND_META = {
  rising: { Icon: TrendingUp, cls: 'text-emerald-600' },
  falling: { Icon: TrendingDown, cls: 'text-red-600' },
  flat: { Icon: Minus, cls: 'text-muted-foreground' },
} as const;

/** PAP/POD top movers by velocity, with rising/falling/flat trend indicators. */
export const TopMoversCard: React.FC<TopMoversCardProps> = ({ loading, rows }) => {
  const { t } = useTranslation('inventory');

  return (
    <ChartCard
      title={t('reporting.v2.topMovers', 'Top movers')}
      description={t(
        'reporting.v2.topMoversDesc',
        'Highest velocity SKUs, with momentum vs the prior period.',
      )}
    >
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-9 animate-pulse rounded bg-muted/40" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyChart
          message={t('reporting.v2.noTopMovers', 'No movement recorded in this period.')}
        />
      ) : (
        <div className="max-h-96 overflow-auto rounded-md border">
          <table className="w-full min-w-[28rem] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="py-2 pl-3 pr-3 font-medium">{t('reporting.v2.sku', 'SKU')}</th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.units', 'Units')}
                </th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.velocity', 'Velocity')}
                </th>
                <th className="py-2 pl-3 pr-3 text-right font-medium">
                  {t('reporting.v2.momentum', 'Momentum')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const { Icon, cls } = TREND_META[row.trend];
                const attrs = [row.type, row.color, row.size].filter(Boolean).join(' · ');
                return (
                  <tr key={row.sku} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pl-3 pr-3">
                      <p className="font-mono text-xs font-medium">{row.sku}</p>
                      {attrs && (
                        <p className="text-xs text-muted-foreground">{attrs}</p>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">
                      {formatUnits(row.units)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                      {formatRate(row.velocity)}/d
                    </td>
                    <td className="py-2 pl-3 pr-3 text-right">
                      <span
                        className={cn(
                          'inline-flex items-center justify-end gap-1 tabular-nums',
                          cls,
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {row.deltaUnits > 0 ? '+' : ''}
                        {formatUnits(row.deltaUnits)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
};
